import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography,
  Stack
} from '@mui/material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { initializeEstimate } from '../../../services/ContractService/contractEstimateSlice';
import { 
  BasicInformationSection, 
  ProcedureCodesSection, 
  CalculationResultsSection 
} from '../components/sections';
import { CalculateButton, ClearButton } from '../components/commands/estimateCommands';
import { PageLoading as Loading } from '../../common/components';

const ContractEstimateContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  
  const dispatch = useAppDispatch();
  const estimate = useAppSelector(state => state.contractEstimate.currentEstimate);
  const isLoading = useAppSelector(state => state.contractEstimate.isLoading);

  // Initialize estimate on component mount
  useEffect(() => {
    if (company && !estimate) {
      dispatch(initializeEstimate({ company }));
    }
  }, [company, estimate, dispatch]);

  if (!company) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" px={1}>
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Missing required parameters. Please specify company to view contract estimates.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Company: {company || 'Not specified'}
              </Typography>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  if (!estimate) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center" px={1}>
        <PageCard sx={{ minWidth: 800, maxWidth: 1300, width: '100%' }}>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                Contract Estimate
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Create and manage contract estimates for {company}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ClearButton />
              <CalculateButton />
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Stack spacing={3}>
              {/* Basic Information Section */}
              <BasicInformationSection />

              {/* Procedure Codes Section */}
              <ProcedureCodesSection />

              {/* Calculation Results Section (shows after calculation) */}
              <CalculationResultsSection />
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

const ContractEstimate: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <ContractEstimateContent />
    </AuthenticatedTemplate>
  );
};

export default ContractEstimate;